import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Download, Package, Trash2, Upload } from 'lucide-react';
import { api, ApiError, type TenantSkill, type TenantSkillDeploy, type TenantSkillSiteStatus } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { relativeTime } from '@/lib/utils';

// Custom skills installed for every website of one tenant. The tenant keeps
// them (they're deployed to each website like a built-in skill and survive
// fleet skill deploys); this card installs, updates and removes them, and
// shows where each one actually is.

const STATUS_LABEL: Record<TenantSkillSiteStatus, string> = {
  installed: 'Installed',
  local_edits: 'Edited on the website — kept as is',
  forked: 'The website made its own version',
  own_skill: 'The website has its own skill with this name',
  opted_out: 'Uninstalled on the website',
  pending: 'Not installed yet',
};

interface Upload {
  file: File;
  // Set when updating a listed skill; null = new skill (slug from the zip).
  slug: string | null;
  replace: boolean;
  replaceBuiltin: boolean;
}

interface Question {
  kind: 'slug_exists' | 'builtin_exists';
  slug: string;
  name: string;
  upload: Upload;
}

interface InstallResult {
  skill: TenantSkill | null;
  created: boolean;
  deploy: TenantSkillDeploy;
}

export function TenantSkillsCard({ tenantId, running }: { tenantId: string; running: boolean }) {
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [updateSlug, setUpdateSlug] = useState<string | null>(null);
  const [question, setQuestion] = useState<Question | null>(null);
  const [removing, setRemoving] = useState<TenantSkill | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);

  const skills = useQuery({
    queryKey: ['tenant-skills', tenantId],
    queryFn: () =>
      api.get<{ skills: TenantSkill[]; unavailable?: string; message?: string }>(
        `/api/tenants/${tenantId}/skills`
      ),
    // Not polled: this reads from the tenant container (see OpsEventsCard).
    enabled: running,
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['tenant-skills', tenantId] });
    qc.invalidateQueries({ queryKey: ['activity', { target_kind: 'tenant', target_id: tenantId }] });
  };

  const install = useMutation({
    mutationFn: (u: Upload) => {
      const q = new URLSearchParams({ filename: u.file.name });
      if (u.slug) q.set('slug', u.slug);
      if (u.replace) q.set('replace', '1');
      if (u.replaceBuiltin) q.set('replace_builtin', '1');
      return api.upload<InstallResult>(`/api/tenants/${tenantId}/skills?${q}`, u.file);
    },
    onSuccess: ({ skill, created, deploy }) => {
      setQuestion(null);
      const name = skill?.name ?? 'The skill';
      const on = skill ? skill.sites.filter((s) => s.status === 'installed').length : 0;
      const failed = deploy.failed.length
        ? ` ${deploy.failed.length} website(s) could not be updated — see the list below.`
        : '';
      setNotice({
        ok: deploy.failed.length === 0,
        text: `${created ? 'Installed' : 'Updated'} “${name}” — on ${on} of ${skill?.sites.length ?? 0} websites.${failed}`,
      });
      refresh();
    },
    onError: (err: Error, u) => {
      const payload = err instanceof ApiError ? (err.payload as { error?: string; slug?: string; name?: string }) : {};
      if (err instanceof ApiError && err.status === 409 && (payload.error === 'slug_exists' || payload.error === 'builtin_exists')) {
        setQuestion({ kind: payload.error, slug: payload.slug ?? '', name: payload.name ?? '', upload: u });
        return;
      }
      setQuestion(null);
      setNotice({ ok: false, text: err.message });
    },
  });

  const remove = useMutation({
    mutationFn: (slug: string) =>
      api.del<{ removed: string; deploy: TenantSkillDeploy }>(
        `/api/tenants/${tenantId}/skills/${encodeURIComponent(slug)}`
      ),
    onSuccess: (data) => {
      setRemoving(null);
      const { skipped, adopted = 0, failed } = data.deploy;
      const kept = skipped + adopted ? ` ${skipped + adopted} website(s) had edited their copy and keep it.` : '';
      const failedText = failed.length ? ` ${failed.length} website(s) could not be updated.` : '';
      setNotice({ ok: failed.length === 0, text: `Removed “${data.removed}” from this tenant's websites.${kept}${failedText}` });
      refresh();
    },
    onError: (err: Error) => {
      setRemoving(null);
      setNotice({ ok: false, text: err.message });
    },
  });

  const pickFile = (slug: string | null) => {
    setUpdateSlug(slug);
    setNotice(null);
    fileInput.current?.click();
  };

  const onFile = (file: File | undefined) => {
    if (!file) return;
    // Updating a listed skill is a replace by definition — no question needed.
    install.mutate({ file, slug: updateSlug, replace: !!updateSlug, replaceBuiltin: false });
    if (fileInput.current) fileInput.current.value = '';
  };

  const list = skills.data?.skills ?? [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <CardTitle>Skills</CardTitle>
            <p className="text-xs text-slate-500 mt-0.5">
              Custom skills for every website of this tenant. Built-in skill deploys keep them.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => pickFile(null)}
            disabled={!running || install.isPending}
            title={running ? 'Upload a skill folder as a .zip (SKILL.md at its top)' : 'Start the tenant first'}
          >
            <Upload size={14} />
            {install.isPending ? 'Installing…' : 'Install skill'}
          </Button>
          <input
            ref={fileInput}
            type="file"
            accept=".zip,application/zip"
            className="hidden"
            onChange={(e) => onFile(e.target.files?.[0])}
          />
        </div>
      </CardHeader>
      <CardBody className="p-0">
        {notice && (
          <div
            className={`px-5 py-2.5 text-xs border-b border-slate-100 ${
              notice.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'
            }`}
          >
            {notice.text}
          </div>
        )}
        {!running ? (
          <div className="p-5 text-sm text-slate-500">Start the tenant to see and manage its skills.</div>
        ) : skills.isLoading ? (
          <div className="p-5 text-sm text-slate-500">Loading…</div>
        ) : skills.isError ? (
          <div className="p-5 text-sm text-red-600">{(skills.error as Error).message}</div>
        ) : skills.data?.unavailable ? (
          <div className="p-5 text-sm text-slate-500">
            Can't read this tenant's skills: {skills.data.message ?? skills.data.unavailable}
          </div>
        ) : !list.length ? (
          <div className="p-5 text-sm text-slate-500">
            No custom skills yet. Install one from a .zip of the skill folder (with SKILL.md at its top) —
            for example a skill downloaded from a website's Skill Builder.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {list.map((skill) => {
              const installed = skill.sites.filter((s) => s.status === 'installed').length;
              const others = skill.sites.filter((s) => s.status !== 'installed');
              const open = expanded === skill.slug;
              return (
                <li key={skill.slug} className="px-5 py-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex items-start gap-3">
                      <Package size={16} className="text-slate-400 mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-slate-900">{skill.name}</span>
                          {skill.slug !== skill.name && (
                            <span className="font-mono text-xs text-slate-500">{skill.slug}</span>
                          )}
                          {skill.replacesBuiltin && <Badge tone="info">replaces built-in</Badge>}
                        </div>
                        {skill.description && (
                          <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{skill.description}</p>
                        )}
                        <button
                          type="button"
                          onClick={() => setExpanded(open ? null : skill.slug)}
                          className="mt-1 inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
                          disabled={!skill.sites.length}
                        >
                          {skill.sites.length > 0 && (open ? <ChevronDown size={12} /> : <ChevronRight size={12} />)}
                          On {installed} of {skill.sites.length} websites
                          {others.length > 0 && <Badge tone="warning">{others.length} not installed</Badge>}
                        </button>
                        <div className="text-xs text-slate-400 mt-0.5">
                          {skill.fileCount} {skill.fileCount === 1 ? 'file' : 'files'} · updated{' '}
                          {relativeTime(skill.updatedAt)}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <a
                        href={`/api/tenants/${tenantId}/skills/${encodeURIComponent(skill.slug)}/download`}
                        className="inline-flex items-center justify-center gap-1.5 rounded-md h-8 px-3 text-xs font-medium text-slate-700 hover:bg-slate-100"
                        title="Download the installed skill as a .zip"
                      >
                        <Download size={14} />
                      </a>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => pickFile(skill.slug)}
                        disabled={install.isPending}
                        title="Upload a new version (.zip) of this skill"
                      >
                        <Upload size={14} />
                        New version
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRemoving(skill)}
                        disabled={remove.isPending}
                        title="Remove from every website of this tenant"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                  {open && (
                    <ul className="mt-2 ml-7 space-y-1">
                      {skill.sites.map((site) => (
                        <li key={site.id} className="flex items-center justify-between gap-3 text-xs">
                          <span className="font-mono text-slate-600 truncate">{site.url ?? site.id}</span>
                          <span className={site.status === 'installed' ? 'text-emerald-700' : 'text-amber-700'}>
                            {STATUS_LABEL[site.status] ?? site.status}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>

      <Dialog
        open={question !== null}
        onOpenChange={(o) => !o && !install.isPending && setQuestion(null)}
        title={question?.kind === 'builtin_exists' ? 'Replace a built-in skill?' : 'Replace skill?'}
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setQuestion(null)} disabled={install.isPending}>
              Cancel
            </Button>
            <Button
              disabled={install.isPending}
              onClick={() =>
                question &&
                install.mutate({
                  ...question.upload,
                  slug: question.slug || question.upload.slug,
                  replace: question.kind === 'slug_exists' || question.upload.replace,
                  replaceBuiltin: question.kind === 'builtin_exists' || question.upload.replaceBuiltin,
                })
              }
            >
              {install.isPending ? 'Installing…' : 'Replace'}
            </Button>
          </>
        }
      >
        {question?.kind === 'builtin_exists' ? (
          <p className="text-sm text-slate-700">
            <span className="font-mono">{question.slug}</span> is a built-in Godmode skill. Your version
            replaces it on every website of this tenant. If you remove yours later, the websites switch back
            to the built-in.
          </p>
        ) : (
          <p className="text-sm text-slate-700">
            This tenant already has the skill <span className="font-mono">{question?.slug}</span>. Replace it
            with the uploaded version? Every website gets the new version, except websites that edited their
            own copy.
          </p>
        )}
      </Dialog>

      <Dialog
        open={removing !== null}
        onOpenChange={(o) => !o && !remove.isPending && setRemoving(null)}
        title="Remove skill?"
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setRemoving(null)} disabled={remove.isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => removing && remove.mutate(removing.slug)}
            >
              {remove.isPending ? 'Removing…' : 'Remove'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-700">
          Remove “{removing?.name}” from every website of this tenant? Websites that edited their copy, or made
          their own version, keep theirs.
          {removing?.replacesBuiltin && ' The websites switch back to the built-in skill.'}
        </p>
      </Dialog>
    </Card>
  );
}
